import { useEffect, useRef, useState } from 'react';
import { User } from 'firebase/auth';
import { isFeatureEnabled } from '../utils/featureFlags';
import { useHostedServer } from './useHostedServer';

const REUSABLE_STATUSES = new Set(['provisioning', 'active', 'stopped']);

function bootstrapStorageKey(userId: string) {
  return `nimah_actual_bootstrap_password_${userId}`;
}

/**
 * Automatically provisions a Nimah-hosted Actual instance once the user is signed in
 * and no reusable hosted server record exists yet.
 */
export function useAutoProvisionHostedServer(user: User | null) {
  const hostingEnabled = isFeatureEnabled('ENABLE_ACTUAL_HOSTING');
  const {
    server,
    isLoading,
    isBusy,
    createServer,
    startServer,
    touchServer,
    error: queryError
  } = useHostedServer(user);

  const attemptedForUserRef = useRef<string | null>(null);
  const [provisionError, setProvisionError] = useState<string | null>(null);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [bootstrapPassword, setBootstrapPassword] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setBootstrapPassword(null);
      return;
    }
    try {
      const stored = sessionStorage.getItem(bootstrapStorageKey(user.uid));
      if (stored) {
        setBootstrapPassword(stored);
      }
    } catch {
      // ignore storage errors
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      attemptedForUserRef.current = null;
      setProvisionError(null);
      setIsProvisioning(false);
      return;
    }

    if (!hostingEnabled || isLoading) {
      return;
    }

    if (server && REUSABLE_STATUSES.has(server.status)) {
      setProvisionError(null);
      setIsProvisioning(false);
      return;
    }

    if (server?.status === 'failed') {
      setProvisionError(server.lastError || 'Provisioning failed');
      setIsProvisioning(false);
      // Keep attempt guard clear so retryProvision / a later effect can try again.
      attemptedForUserRef.current = null;
      return;
    }

    if (attemptedForUserRef.current === user.uid) {
      return;
    }

    attemptedForUserRef.current = user.uid;
    setIsProvisioning(true);
    setProvisionError(null);

    createServer()
      .then((result) => {
        if (result.server?.status === 'failed') {
          setProvisionError(result.server.lastError || 'Provisioning failed');
          attemptedForUserRef.current = null;
          return;
        }
        if (result.bootstrapPassword) {
          setBootstrapPassword(result.bootstrapPassword);
          try {
            sessionStorage.setItem(bootstrapStorageKey(user.uid), result.bootstrapPassword);
          } catch {
            // ignore
          }
        }
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : 'REQUEST_FAILED';
        setProvisionError(message);
        // Allow a manual retry by clearing the attempt guard on failure
        attemptedForUserRef.current = null;
      })
      .finally(() => {
        setIsProvisioning(false);
      });
  }, [user, hostingEnabled, isLoading, server, createServer]);

  const retryProvision = async () => {
    if (!user || !hostingEnabled) return;
    attemptedForUserRef.current = user.uid;
    setIsProvisioning(true);
    setProvisionError(null);
    try {
      const result = await createServer();
      if (result.server?.status === 'failed') {
        setProvisionError(result.server.lastError || 'Provisioning failed');
        attemptedForUserRef.current = null;
        return;
      }
      if (result.bootstrapPassword) {
        setBootstrapPassword(result.bootstrapPassword);
        try {
          sessionStorage.setItem(bootstrapStorageKey(user.uid), result.bootstrapPassword);
        } catch {
          // ignore
        }
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'REQUEST_FAILED';
      setProvisionError(message);
      attemptedForUserRef.current = null;
    } finally {
      setIsProvisioning(false);
    }
  };

  const clearBootstrapPassword = () => {
    setBootstrapPassword(null);
    if (user) {
      try {
        sessionStorage.removeItem(bootstrapStorageKey(user.uid));
      } catch {
        // ignore
      }
    }
  };

  return {
    hostingEnabled,
    server,
    isLoading,
    isBusy: isBusy || isProvisioning,
    isAutoProvisioning: isProvisioning,
    provisionError,
    queryError,
    bootstrapPassword,
    clearBootstrapPassword,
    retryProvision,
    startServer,
    touchServer
  };
}
